import { useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import { UploadSpreadsheetStepProps } from './types';
import { colors } from '@src/styles';
import { ghostButtonSX, monoFontFamily } from '../../scriptsStyles';

const ACCEPTED_EXTENSIONS = ['.xlsx', '.xls', '.csv'];

const isAcceptedFile = (file: File): boolean =>
  ACCEPTED_EXTENSIONS.some((extension) => file.name.toLowerCase().endsWith(extension));

const formatFileSize = (bytes: number): string =>
  bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

export const UploadSpreadsheetStep = ({ selectedFile, onFileSelect }: UploadSpreadsheetStepProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const [rejectedName, setRejectedName] = useState<string | null>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setRejectedName(null);
    onFileSelect(file || null);
  };

  const handleDrop = (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files?.[0];
    if (!file) return;
    if (!isAcceptedFile(file)) {
      setRejectedName(file.name);
      return;
    }
    setRejectedName(null);
    onFileSelect(file);
  };

  return (
    <Box>
      <input
        accept=".xlsx,.xls,.csv"
        style={{ display: 'none' }}
        id="file-upload"
        type="file"
        onChange={handleFileChange}
      />

      {selectedFile ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            padding: '14px 16px',
            borderRadius: '12px',
            border: `1px solid ${colors.status.concluded}55`,
            backgroundColor: `${colors.status.concluded}0F`,
          }}
        >
          <Box
            sx={{
              width: 38,
              height: 38,
              flexShrink: 0,
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: `${colors.status.concluded}1F`,
            }}
          >
            <TableChartOutlinedIcon sx={{ fontSize: 20, color: `${colors.status.concluded} !important` }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography noWrap title={selectedFile.name} sx={{ fontSize: '13.5px', fontWeight: 500 }}>
              {selectedFile.name}
            </Typography>
            <Typography sx={{ fontFamily: monoFontFamily, fontSize: '11px', color: colors.brand.textMuted }}>
              {formatFileSize(selectedFile.size)} · pronta para envio
            </Typography>
          </Box>
          <label htmlFor="file-upload">
            <Button component="span" sx={ghostButtonSX}>
              Trocar
            </Button>
          </label>
        </Box>
      ) : (
        <Box
          component="label"
          htmlFor="file-upload"
          onDragOver={(event: React.DragEvent<HTMLLabelElement>) => {
            event.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            minHeight: 170,
            padding: 3,
            borderRadius: '12px',
            border: `1.5px dashed ${isDragging ? colors.primary.yellow : 'rgba(255, 255, 255, 0.14)'}`,
            backgroundColor: isDragging ? 'rgba(255, 160, 21, 0.06)' : colors.background.primary,
            cursor: 'pointer',
            textAlign: 'center',
            transition: 'border-color 0.15s ease, background-color 0.15s ease',
            '&:hover': { borderColor: 'rgba(255, 160, 21, 0.6)' },
          }}
        >
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 160, 21, 0.12)',
            }}
          >
            <CloudUploadOutlinedIcon sx={{ fontSize: 22, color: `${colors.primary.yellow} !important` }} />
          </Box>
          <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
            Arraste a planilha aqui ou{' '}
            <Box component="span" sx={{ color: colors.primary.yellow }}>
              clique para escolher
            </Box>
          </Typography>
          <Typography sx={{ fontFamily: monoFontFamily, fontSize: '11px', color: colors.brand.textMuted }}>
            .xlsx · .xls · .csv
          </Typography>
        </Box>
      )}

      {rejectedName && (
        <Typography sx={{ mt: 1.25, fontSize: '12.5px', color: colors.status.rejected }}>
          “{rejectedName}” não é uma planilha aceita. Use .xlsx, .xls ou .csv.
        </Typography>
      )}
    </Box>
  );
};
